import { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import {
  Button,
  IconHighlight,
  ModalLayout,
  SearchInput,
  SelectableChip,
  StatusMessage,
} from '@aimsos/ui';

import { useModalEscape } from '../hooks/useModalEscape.js';
import { INTEGRATIONS } from '../constants/integrations.js';
import { ALL_INTEGRATIONS, useEntityPicker } from '../hooks/useEntityPicker.js';
import { useScrollableModalBody } from '../hooks/useScrollableModalBody.js';
import EntityPickerCard from './EntityPickerCard.jsx';
import InlineNote from './InlineNote.jsx';
import { ModalFooter } from './parts';
import PickerOptionGroup from './PickerOptionGroup.jsx';
import { PickerError, PickerSkeleton } from './PickerStates.jsx';

import { Boxes, SearchX } from 'lucide-react';

const PILLS = [
  { id: ALL_INTEGRATIONS, labelKey: 'widgetBuilder.entityPicker.allSources' },
  ...INTEGRATIONS,
];

/**
 * `Choose an entity` — the full catalog (CT-5192 / DS-3).
 *
 * `ModalLayout` + `useBodyPortal`, so there is no `createPortal`, no
 * `fixed inset-0`, no scroll lock and no focus trap here: the DS owns all of it,
 * including `Esc`. There is deliberately no second `Esc` handler.
 *
 * None of ARP-932's scale UX is here — no `Recently used` rail, no domain
 * category pills, no record counts, no PII badge. They are not in the V1
 * prototype, and a placeholder would be worse than their absence.
 */
function EntityPickerModal({ isOpen, committedEntityRef, onClose, onCommit }) {
  const { t } = useTranslation('dashboards');
  const { bodyClassName, bodyProps } = useScrollableModalBody();
  const searchRef = useRef(null);

  const {
    entities,
    visibleEntities,
    draftEntityRef,
    searchTerm,
    activeIntegration,
    canUse,
    isLoading,
    error,
    refetch,
    setDraftEntityRef,
    setSearchTerm,
    setActiveIntegration,
    resetDraft,
  } = useEntityPicker({ committedEntityRef, isOpen });

  // Focus the search once per open, not on every re-render — re-focusing while
  // the user types would fight them for the caret.
  useEffect(() => {
    if (!isOpen) return;
    searchRef.current?.querySelector('input')?.focus();
  }, [isOpen]);

  const handleCancel = useCallback(() => {
    resetDraft();
    onClose?.();
  }, [onClose, resetDraft]);

  const handleUse = useCallback(() => {
    if (!draftEntityRef) return;
    // The integration travels with the selection so the inline picker can move
    // its pill to match. Without it the inline list would keep showing the old
    // integration with nothing selected in it.
    const entity = visibleEntities.find((candidate) => candidate.id === draftEntityRef);
    onCommit?.(draftEntityRef, entity?.integration, entity?.name);
  }, [draftEntityRef, onCommit, visibleEntities]);

  const handleSearchChange = useCallback(
    (event) => setSearchTerm(event.target.value),
    [setSearchTerm],
  );

  const handleClearSearch = useCallback(() => setSearchTerm(''), [setSearchTerm]);

  const integrationLabels = useMemo(
    () => Object.fromEntries(INTEGRATIONS.map(({ id, labelKey }) => [id, t(labelKey)])),
    [t],
  );

  const governanceLabels = useMemo(
    () => ({
      ungoverned: t('widgetBuilder.data.entities.ungoverned'),
      pii: t('widgetBuilder.data.entities.pii'),
    }),
    [t],
  );

  const selectedName = useMemo(
    () => entities.find((entity) => entity.id === draftEntityRef)?.name ?? '',
    [draftEntityRef, entities],
  );

  useModalEscape(isOpen, handleCancel);
  if (!isOpen) return null;

  const grid = () => {
    if (isLoading) return <PickerSkeleton count={6} height={72} />;
    if (error) {
      return (
        <PickerError
          title={t('widgetBuilder.data.entities.errorTitle')}
          subtitle={t('widgetBuilder.data.entities.errorSubtitle')}
          retryLabel={t('widgetBuilder.data.retry')}
          onRetry={refetch}
        />
      );
    }
    if (visibleEntities.length === 0) {
      return (
        <div className="flex flex-col items-center gap-3">
          <StatusMessage
            icon={SearchX}
            title={t('widgetBuilder.entityPicker.emptyTitle')}
            subtitle={t('widgetBuilder.entityPicker.emptySubtitle')}
          />
          <Button variant="secondary" size="sm" onClick={handleClearSearch}>
            {t('widgetBuilder.entityPicker.clearSearch')}
          </Button>
        </div>
      );
    }

    return (
      <PickerOptionGroup
        ariaLabel={t('widgetBuilder.entityPicker.title')}
        columns={2}
        className="grid gap-3 sm:grid-cols-2"
      >
        {visibleEntities.map((entity) => (
          <EntityPickerCard
            key={entity.id}
            entity={entity}
            integrationLabel={integrationLabels[entity.integration] ?? entity.integration}
            metaLabel={t('widgetBuilder.data.entities.memberCount', { count: entity.memberCount })}
            ungovernedLabel={governanceLabels.ungoverned}
            piiLabel={governanceLabels.pii}
            selected={draftEntityRef === entity.id}
            onSelect={setDraftEntityRef}
          />
        ))}
      </PickerOptionGroup>
    );
  };

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleCancel}
      useBodyPortal
      fillHeight
      maxWidthClassName="max-w-3xl"
      titlePrefix={<IconHighlight icon={Boxes} bgColor="Primary" iconColor="PrimaryAlt" size="medium" />}
      title={t('widgetBuilder.entityPicker.title')}
      subtitle={t('widgetBuilder.entityPicker.subtitle')}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex flex-col gap-3 px-6 pb-3 pt-4">
          <div ref={searchRef}>
            <SearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={handleClearSearch}
              placeholder={t('widgetBuilder.entityPicker.searchPlaceholder')}
              aria-label={t('widgetBuilder.entityPicker.searchPlaceholder')}
            />
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            {PILLS.map(({ id, labelKey }) => (
              <SelectableChip
                key={id}
                size="m"
                selected={activeIntegration === id}
                onClick={() => setActiveIntegration(id)}
              >
                {t(labelKey)}
              </SelectableChip>
            ))}
          </div>
        </div>

        <div className={`${bodyClassName} px-6 pb-4`} {...bodyProps}>
          {grid()}
        </div>

        <div className="px-6 pb-3">
          <InlineNote>{t('widgetBuilder.entityPicker.moreAvailable')}</InlineNote>
        </div>

        <ModalFooter
          cancelLabel={t('widgetBuilder.entityPicker.cancel')}
          onCancel={handleCancel}
          submitDisabled={!canUse}
          statusText={
            canUse
              ? t('widgetBuilder.entityPicker.selected', { name: selectedName })
              : t('widgetBuilder.entityPicker.selectToContinue')
          }
          primaryAction={
            <Button variant="primary" onClick={handleUse} className="!text-[12px]">
              {t('widgetBuilder.entityPicker.use')}
            </Button>
          }
        />
      </div>
    </ModalLayout>
  );
}

EntityPickerModal.propTypes = {
  isOpen: PropTypes.bool,
  committedEntityRef: PropTypes.string,
  onClose: PropTypes.func,
  /** `(entityRef, integration, name) => void` */
  onCommit: PropTypes.func,
};

export default EntityPickerModal;
